/** One-shot buffer playback with a hard cap on concurrent voices. */
export interface PlayOptions {
  /** Context time to start; defaults to now. */
  at?: number;
  gain?: number;
  rate?: number;
  /** -1 (left) .. 1 (right). */
  pan?: number;
  /** Optional lowpass cutoff for distance. */
  lowpass?: number;
  /** 0 = expendable texture, 1 = normal, 2 = must play (crash, finish). */
  priority?: number;
}

export class OneShots {
  private active = 0;
  private textures = 0;
  private readonly stereo: boolean;
  constructor(private ctx: BaseAudioContext, private max = 22, private maxTextures = 7) {
    this.stereo = typeof ctx.createStereoPanner === 'function';
  }
  get count() { return this.active; }
  play(buffer: AudioBuffer, out: AudioNode, o: PlayOptions = {}): boolean {
    const priority = o.priority ?? 1;
    if (this.active >= this.max && priority < 2) return false;
    if (priority === 0 && this.textures >= this.maxTextures) return false;
    const c = this.ctx, at = Math.max(c.currentTime, o.at ?? c.currentTime);
    const src = c.createBufferSource(); src.buffer = buffer; src.playbackRate.value = o.rate ?? 1;
    const gain = c.createGain(); gain.gain.value = o.gain ?? 1;
    const chain: AudioNode[] = [src];
    if (o.lowpass) { const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = Math.min(o.lowpass, c.sampleRate * .45); f.Q.value = .6; chain.push(f); }
    chain.push(gain);
    if (o.pan && this.stereo) { const p = c.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, o.pan)); chain.push(p); }
    for (let i = 0; i < chain.length - 1; i++) chain[i].connect(chain[i + 1]);
    chain[chain.length - 1].connect(out);
    this.active++; if (priority === 0) this.textures++;
    src.onended = () => { this.active--; if (priority === 0) this.textures--; for (const node of chain) { try { node.disconnect(); } catch { /* already gone */ } } };
    src.start(at);
    return true;
  }
}
